import { createSlice } from '@reduxjs/toolkit';
import { DateRange } from './dateRangeSlice';

export type TicketsFilterState = {
  otherPriority: boolean,
  lowPriority: boolean,
  mediumPriority: boolean,
  highPriority: boolean,
  criticalPriority: boolean,
  doneFilter: boolean,
  inProgressFilter: boolean,
  notStartedFilter: boolean,
  selectedAssignee: string,
  selectedLocation: string,
  selectedAssetId: string,
  selectedAssetName?: string,
  selectedUserGroupId: string,
  slaOverdueFilter: boolean,
  slaSort: string,
  selectedDateRange: DateRange | null,
  saveFilterToggled?: boolean,
  selectedTemplateId?: string | null,
  selectedIssueId?: string | null,
}

const initialState: TicketsFilterState = {
  otherPriority: false,
  lowPriority: false,
  mediumPriority: false,
  highPriority: false,
  criticalPriority: false,
  doneFilter: false,
  inProgressFilter: false,
  notStartedFilter: false,
  selectedAssignee: '',
  selectedLocation: '',
  selectedAssetId: '',
  selectedAssetName: null,
  selectedUserGroupId: '',
  slaOverdueFilter: false,
  slaSort: '',
  selectedDateRange: null,
  saveFilterToggled: false,
  selectedTemplateId: null,
  selectedIssueId: null,
};

const ticketsFilterSlice = createSlice({
  name: 'ticketsFilter',
  initialState,
  reducers: {
    updateTicketsFilter(state: TicketsFilterState, action: { payload: TicketsFilterState }) {
      state.otherPriority = action.payload.otherPriority;
      state.lowPriority = action.payload.lowPriority;
      state.mediumPriority = action.payload.mediumPriority;
      state.highPriority = action.payload.highPriority;
      state.criticalPriority = action.payload.criticalPriority;
      state.doneFilter = action.payload.doneFilter;
      state.inProgressFilter = action.payload.inProgressFilter;
      state.notStartedFilter = action.payload.notStartedFilter;
      state.selectedAssignee = action.payload.selectedAssignee;
      state.selectedLocation = action.payload.selectedLocation;
      state.selectedAssetId = action.payload.selectedAssetId;
      state.selectedAssetName = action.payload.selectedAssetName;
      state.selectedUserGroupId = action.payload.selectedUserGroupId;
      state.slaOverdueFilter = action.payload.slaOverdueFilter;
      state.slaSort = action.payload.slaSort;
      state.selectedDateRange = action.payload.selectedDateRange;
      state.saveFilterToggled = action.payload.saveFilterToggled ?? state.saveFilterToggled;
      state.selectedTemplateId = action.payload.selectedTemplateId;
      state.selectedIssueId = action.payload.selectedIssueId;
    },
    updateTicketsFilterSaveFilterToggled(state: TicketsFilterState, action: { payload: boolean }) {
      state.saveFilterToggled = action.payload;
    },
    updateTicketsFilterOther(state: TicketsFilterState, action: { payload: boolean }) {
      state.otherPriority = action.payload;
    },
    updateTicketsFilterLow(state: TicketsFilterState, action: { payload: boolean }) {
      state.lowPriority = action.payload;
    },
    updateTicketsFilterMed(state: TicketsFilterState, action: { payload: boolean }) {
      state.mediumPriority = action.payload;
    },
    updateTicketsFilterHigh(state: TicketsFilterState, action: { payload: boolean }) {
      state.highPriority = action.payload;
    },
    updateTicketsFilterCritical(state: TicketsFilterState, action: { payload: boolean }) {
      state.criticalPriority = action.payload;
    },
    updateTicketsFilterDone(state: TicketsFilterState, action: { payload: boolean }) {
      state.doneFilter = action.payload;
    },
    updateTicketsFilterInProgress(state: TicketsFilterState, action: { payload: boolean }) {
      state.inProgressFilter = action.payload;
    },
    updateTicketsFilterWithoutStatus(state: TicketsFilterState, action: { payload: boolean }) {
      state.notStartedFilter = action.payload;
    },
    updateTicketsFilterAssignee(state: TicketsFilterState, action: { payload: string }) {
      state.selectedAssignee = action.payload;
    },
    updateTicketsFilterLocation(state: TicketsFilterState, action: { payload: string }) {
      state.selectedLocation = action.payload;
    },
    updateTicketsFilterAssetId(state: TicketsFilterState, action: { payload: string }) {
      state.selectedAssetId = action.payload;
    },
    updateTicketsFilterAssetName(state: TicketsFilterState, action: { payload: string }) {
      state.selectedAssetName = action.payload;
    },
    updateTicketsFilterUserGroup(state: TicketsFilterState, action: { payload: string }) {
      state.selectedUserGroupId = action.payload;
    },
    updateTicketsFilterSlaOverdue(state: TicketsFilterState, action: { payload: boolean }) {
      state.slaOverdueFilter = action.payload;
    },
    updateTicketsFilterSlaSort(state: TicketsFilterState, action: { payload: string }) {
      state.slaSort = action.payload;
    },
    updateTicketsFilterDateRange(state: TicketsFilterState, action: { payload: DateRange | null }) {
      state.selectedDateRange = action.payload;
    },
    resetAllTicketFilters(state: TicketsFilterState) {
      state.otherPriority = false;
      state.lowPriority = false;
      state.mediumPriority = false;
      state.highPriority = false;
      state.criticalPriority = false;
      state.doneFilter = false;
      state.inProgressFilter = false;
      state.notStartedFilter = false;
      state.selectedAssignee = '';
      state.selectedLocation = '';
      state.selectedAssetId = '';
      state.selectedAssetName = null;
      state.selectedUserGroupId = '';
      state.slaOverdueFilter = false;
      state.slaSort = '';
      state.selectedDateRange = null;
      state.selectedTemplateId = null;
      state.selectedIssueId = null;
    },
  },
});

export const {
  updateTicketsFilter, updateTicketsFilterOther, updateTicketsFilterLow, updateTicketsFilterMed,
  updateTicketsFilterHigh, updateTicketsFilterCritical, updateTicketsFilterDone,
  updateTicketsFilterInProgress, updateTicketsFilterWithoutStatus, updateTicketsFilterAssignee,
  updateTicketsFilterLocation, updateTicketsFilterAssetId, updateTicketsFilterUserGroup,
  resetAllTicketFilters, updateTicketsFilterSlaOverdue, updateTicketsFilterSlaSort,
  updateTicketsFilterAssetName, updateTicketsFilterDateRange,
  updateTicketsFilterSaveFilterToggled,
} = ticketsFilterSlice.actions;

export default ticketsFilterSlice.reducer;
